import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useRegisterMutation } from "../../slices/usersApiSlice";

import { toast } from "react-toastify";

const CreateUserScreen = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [role, setRole] = useState("student");

  const [address, setAddress] = useState("");
  const [studentId, setStudentId] = useState("");
  const [batch, setBatch] = useState("");
  const [course, setCourse] = useState("");

  const navigate = useNavigate();

  const [register, { isLoading }] = useRegisterMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    // ============================================
    // Basic Validation
    // ============================================

    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!phone.trim()) {
      toast.error("Phone number is required");
      return;
    }

    if (role === "student" && !studentId.trim()) {
      toast.error("Student ID is required");
      return;
    }

    try {
      await register({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,

        role,

        address: address.trim() || undefined,

        // Student-specific fields
        studentId: role === "student" ? studentId.trim() : undefined,

        batch: role === "student" ? batch.trim() || undefined : undefined,

        course: role === "student" ? course.trim() || undefined : undefined,

        // IMPORTANT:
        // Do NOT send password.
        //
        // Backend creates the user with:
        // password = "123456"
        // mustChangePassword = true
      }).unwrap();

      toast.success(
        `${role === "student" ? "Student" : "Center admin"} created successfully`,
      );

      navigate("/users");
    } catch (err) {
      const message =
        err?.data?.message || err?.error || "User creation failed";

      switch (message) {
        case "Invalid user role":
          toast.error("Invalid user role selected");
          break;

        case "Coaching center required":
          toast.error("Your account is not linked with a coaching center");
          break;

        case "Only coaching center admin can create students":
          toast.error("Only a coaching center administrator can create users");
          break;

        case "User already exists":
          toast.error("A user with this phone number already exists");
          break;

        case "A user with this phone number already exists in this coaching center":
          toast.error(
            "A user with this phone number already exists in this coaching center",
          );
          break;

        case "Student ID is required":
          toast.error("Student ID is required");
          break;

        case "A student with this student ID already exists":
          toast.error("A student with this student ID already exists");
          break;

        default:
          toast.error(message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-4xl bg-white/80 backdrop-blur-lg border border-white/30 shadow-2xl rounded-3xl p-6 md:p-10">
        {/* ====================================== */}
        {/* HEADER */}
        {/* ====================================== */}

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
            Create User
          </h1>

          <p className="text-gray-500 mt-2">
            Add a student or center administrator
          </p>
        </div>

        {/* ====================================== */}
        {/* TEMPORARY PASSWORD INFO */}
        {/* ====================================== */}

        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <h3 className="font-semibold text-amber-800">Temporary Password</h3>

          <p className="text-sm text-amber-700 mt-1">
            The new user will be created with the temporary password{" "}
            <strong>123456</strong>. They must change this password when they
            log in for the first time.
          </p>
        </div>

        {/* ====================================== */}
        {/* FORM */}
        {/* ====================================== */}

        <form
          onSubmit={submitHandler}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {/* Name */}

          <Input
            label="Full Name"
            value={name}
            setValue={setName}
            placeholder="John Doe"
            required
          />

          {/* Phone */}

          <Input
            label="Phone Number"
            value={phone}
            setValue={setPhone}
            placeholder="9876543210"
            type="tel"
            required
          />

          {/* Email */}

          <Input
            label="Email Address"
            value={email}
            setValue={setEmail}
            placeholder="john@example.com"
            type="email"
          />

          {/* Role */}

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              User Role
            </label>

            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value);

                // Clear student fields when switching
                // to center admin.
                if (e.target.value === "centerAdmin") {
                  setStudentId("");
                  setBatch("");
                  setCourse("");
                }
              }}
              className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="student">Student</option>

              <option value="centerAdmin">Center Administrator</option>
            </select>
          </div>

          {/* ==================================== */}
          {/* STUDENT FIELDS */}
          {/* ==================================== */}

          {role === "student" && (
            <>
              <Input
                label="Student ID"
                value={studentId}
                setValue={setStudentId}
                placeholder="STU001"
                required
              />

              <Input
                label="Batch"
                value={batch}
                setValue={setBatch}
                placeholder="Morning Batch"
              />

              <Input
                label="Course"
                value={course}
                setValue={setCourse}
                placeholder="NEET / JEE / UPSC"
              />
            </>
          )}

          {/* ==================================== */}
          {/* ADDRESS */}
          {/* ==================================== */}

          <div className="md:col-span-2">
            <Input
              label="Address"
              value={address}
              setValue={setAddress}
              placeholder="Enter address"
            />
          </div>

          {/* ==================================== */}
          {/* CREATE BUTTON */}
          {/* ==================================== */}

          <div className="md:col-span-2 pt-3">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {isLoading ? "Creating User..." : "Create User"}
            </button>
          </div>
        </form>

        {/* ====================================== */}
        {/* FOOTER */}
        {/* ====================================== */}

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate("/users")}
            className="text-sm text-gray-600 hover:text-emerald-600"
          >
            ← Back to Users
          </button>
        </div>
      </div>
    </div>
  );
};

const Input = ({
  label,
  value,
  setValue,
  placeholder,
  type = "text",
  required = false,
}) => (
  <div>
    <label className="block mb-2 text-sm font-medium text-gray-700">
      {label}

      {required && <span className="text-red-500 ml-1">*</span>}
    </label>

    <input
      type={type}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
    />
  </div>
);

export default CreateUserScreen;
